import { ClaimSearchCardView } from '@percent/cause-dashboard/app/auth/claim/ClaimSearchCardView/ClaimSearchCardView'
import { ErrorView, Loader, PaginationActions } from '@percent/cause-dashboard/common/components'
import {
  useAuthDispatch,
  useAuthState,
  useCausesPortalAnalytics,
  useMutation,
  useQueryList
} from '@percent/cause-dashboard/common/hooks'
import { SignUpEventName } from '@percent/cause-dashboard/common/hooks/useCausesDashboardAnalytics/causeDashboardAnalytics.types'
import { SET_ORGANISATION_DETAIL, SET_SELECTED_CHARITY, selectAuthState } from '@percent/cause-dashboard/context/auth'
import { useServices } from '@percent/cause-dashboard/context/serviceContext/ServiceContext'
import { Organisation } from '@percent/cause-dashboard/services/cause/causeService.types'
import { Claim } from '@percent/cause-dashboard/services/claim/claimService.types'
import { Button, ConfirmationModal, Grid, Modal, Typography } from '@percent/lemonade'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation } from 'react-router-dom'
import { OrganizationNotFound } from '../OrganizationNotFound/OrganizationNotFound'
import { RequestToJoinOrganisation } from '../RequestToJoinOrganisation/RequestToJoinOrganisation'
import styles from './ClaimSearchResults.module.scss'

type ClaimSearchResultsProps = {
  claimResults: {
    selectedCharityCountryCode: string
    selectedCharityName: string
  }
}

export function ClaimSearchResults({ claimResults }: ClaimSearchResultsProps) {
  const { t } = useTranslation()
  const { causeService, claimService } = useServices()
  const [
    { data, status, isLoading, errorMessage, totalResults },
    { query: searchCharity, nextPage, previousPage, refresh }
  ] = useQueryList(
    causeService.getCauseSearch,
    { countryCode: claimResults.selectedCharityCountryCode, query: claimResults.selectedCharityName },
    !!claimResults.selectedCharityName
  )
  const { selectedCharityDispatch, authDispatch } = useAuthDispatch()
  const navigate = useNavigate()
  const { search } = useLocation()
  const urlParams = new URLSearchParams(search)

  const {
    authState,
    selectedCharityState: { selectedCharityName }
  } = useAuthState()
  const { userVerified, claimMade } = selectAuthState(authState)

  const utmParams = {
    utm_source: urlParams.get('utm_source'),
    utm_medium: urlParams.get('utm_medium'),
    utm_campaign: urlParams.get('utm_campaign'),
    utm_term: urlParams.get('utm_term'),
    utm_content: urlParams.get('utm_content')
  }

  useEffect(() => {
    if (data) {
      track(SignUpEventName.SEARCH_RESULTS, {
        countryCode: claimResults.selectedCharityCountryCode,
        query: claimResults.selectedCharityName,
        totalResults,
        accountId: authState.user?.id,
        ...utmParams
      })
    }
  }, [data])

  const [{ errorMessage: claimErrorMessage, isLoading: isClaimLoading }, { apiFunc: claimCharity }] = useMutation(
    claimService.postVSClaim,
    ({ organisationId }: Claim) => {
      authDispatch({
        type: SET_ORGANISATION_DETAIL,
        payload: {
          claimOrganisation: {
            id: organisationId
          }
        }
      })
      navigate('/profile')
    }
  )

  const [open, setOpen] = useState(false)
  const [organization, setOrganization] = useState<Organisation | undefined>(undefined)
  const initialRender = useRef(true)
  const errorMsg = errorMessage || claimErrorMessage
  const { track } = useCausesPortalAnalytics()

  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false
    } else {
      searchCharity({
        countryCode: claimResults.selectedCharityCountryCode,
        query: claimResults.selectedCharityName,
        page: 1
      })
    }
  }, [claimResults, searchCharity])

  const handleClose = () => {
    setOpen(false)
  }

  const dipatchFunc = (causeName: string | '') =>
    selectedCharityDispatch({
      type: SET_SELECTED_CHARITY,
      payload: {
        selectedCharity: {
          selectedCharityName: causeName,
          selectedCharityCountryCode: claimResults.selectedCharityCountryCode,
          selectedCharityId: organization?.id || ''
        }
      }
    })

  // eslint-disable-next-line consistent-return
  const handleOnClick = async (org: Organisation, selectedResultRank: number) => {
    await track(SignUpEventName.CLAIM_NONPROFIT, {
      organisationName: org.name,
      selectedResultRank,
      countryCode: claimResults.selectedCharityCountryCode,
      query: claimResults.selectedCharityName,
      accountId: authState.user?.id,
      ...utmParams
    })
    setOrganization(org)
    setOpen(true)
    dipatchFunc(org.name)
  }

  const handleClaim = async () => {
    await track(SignUpEventName.SIGNUP_AND_CLAIM, {
      organisationName: organization?.name,
      accountId: authState.user?.id,
      ...utmParams
    })
    if (userVerified && !claimMade) {
      claimCharity({
        organisationId: organization?.id || ''
      })
    } else {
      const utmString = Object.entries(utmParams)
        .filter(([_, value]) => !!value)
        .map(([key, value]) => `${key}=${value}`)
        .join('&')

      navigate({
        pathname: '/signup',
        search: `?organization-id=${organization?.id}${utmString ? `&${utmString}` : ''}`
      })
      dipatchFunc(selectedCharityName || '')
    }
  }

  const getAddressDisplay = (org: Organisation | undefined) => {
    if (org?.address) {
      if (org?.postcode) {
        return `${org.address} ${org.postcode}`
      }
      return org.address
    }
    return t('dialog.claimSearchResultsTable.noAddress')
  }

  if (isLoading || isClaimLoading) {
    return <Loader />
  }

  if (errorMsg) {
    return <ErrorView errorMessage={errorMsg} />
  }

  if (status === 200 && totalResults === 0) {
    return <OrganizationNotFound />
  }

  if (data) {
    return (
      <>
        <Typography variant="h2" color="secondary" className={styles.claimsText}>
          {t('typography.charitiesFound', { count: totalResults })}
        </Typography>

        {data?.map((cause, idx) => {
          const address = getAddressDisplay(cause)

          return (
            <ClaimSearchCardView
              key={cause.id}
              title={cause.name}
              subTitle={address}
              detail={cause.registryId}
              cardAction={
                cause.claimedAt ? (
                  <RequestToJoinOrganisation organisationId={cause.id} organisationName={cause.name} />
                ) : (
                  <div className={styles.claimButtonContainer}>
                    <Button
                      variant="secondary"
                      size="medium"
                      data-testid="claim-search-claim-button"
                      onClick={() => handleOnClick(cause, idx)}
                      stretch
                    >
                      {t('button.claimCharity')}
                    </Button>
                  </div>
                )
              }
              alignCardToBottom={Boolean(cause.claimedAt)}
            />
          )
        })}
        <Modal open={open} onClose={handleClose}>
          <ConfirmationModal
            title={`${t('dialog.claimSearchResultsTable.title')}`}
            primaryButtonText={t('button.confirm')}
            primaryBtnTestId="claim-charity-btn"
            secondaryButtonText={t('button.cancel')}
            handleClose={handleClose}
            handleSubmit={handleClaim}
          >
            <Typography color="secondary" className={styles.dialogDescription}>
              {t('dialog.claimSearchResultsTable.description')}
            </Typography>
            <Grid gutter={4} className={styles.subtitleContainer}>
              <Grid.Col span={12}>
                <Typography className={styles.subtitle}>{`${selectedCharityName || organization?.name}`}</Typography>
              </Grid.Col>
              <Grid.Col span={4}>
                <Typography className={styles.category}>{t('dialog.claimSearchResultsTable.address')}</Typography>
              </Grid.Col>
              <Grid.Col span={8}>
                <Typography color="textSecondary">{getAddressDisplay(organization)}</Typography>
              </Grid.Col>
              <Grid.Col span={4}>
                <Typography className={styles.category}>
                  {t('dialog.claimSearchResultsTable.registerNumber')}
                </Typography>
              </Grid.Col>
              <Grid.Col span={8}>
                <Typography color="textSecondary">{organization?.registryId}</Typography>
              </Grid.Col>
            </Grid>
          </ConfirmationModal>
        </Modal>
        <div className={styles.footerCell}>
          <PaginationActions count={totalResults} nextPage={nextPage} previousPage={previousPage} refresh={refresh} />
        </div>
      </>
    )
  }

  return null
}
